'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/components/locale-link';
import { useLocaleTranslation } from '@/hooks/use-locale-translation';
import styles from './home-live.module.css';
import { liveEventHref } from './company-href';
import { useHomeSearchMerge } from './home-search-merge';
import { LiveSection } from './LiveSection';
import { ProfileShowcase } from './ProfileShowcase';
import type { LiveEvent } from './live-map-engine';
import { useCompanySearchBox } from './useCompanySearchBox';

// The logo's wordmark (header-logo.tsx): "Firmen" bold, "data" regular.
const WORD = 'Firmendata';
const BOLD_CHARS = 'Firmen'.length;
const TRY_EXAMPLES = ['Nordex', 'Siemens', 'Zalando'];
const SIGNUP_HREF = '/auth/login?screen_hint=signup&returnTo=%2F';

type SearchBox = Omit<ReturnType<typeof useCompanySearchBox>, 'formRef'>;

function SuggestList({ box, listId }: { box: SearchBox; listId: string }) {
  const { t } = useLocaleTranslation();
  if (!box.showList) return null;
  return (
    <ul className={styles.suggest} id={listId} role="listbox">
      {box.hits.map((c, i) => (
        <li
          key={c.euId}
          id={`${listId}-${i}`}
          role="option"
          aria-selected={i === box.active}
          onMouseEnter={() => box.hover(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            box.openCompany(c);
          }}
        >
          <span className={styles.sName}>
            {c.faviconUrl ? (
              <img
                src={c.faviconUrl}
                alt=""
                className={styles.logo}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <span className={styles.logo} aria-hidden="true" />
            )}
            <b>{c.name}</b>
          </span>
          <span>
            {[c.city, c.registerType && c.registerId ? `${c.registerType} ${c.registerId}` : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </li>
      ))}
      {box.empty && (
        <li className={styles.none} role="option" aria-disabled="true" aria-selected={false}>
          {t('homeV2.search.noResults')}
        </li>
      )}
    </ul>
  );
}

/**
 * The homepage: the name, which flips into the company search and, scrolled
 * down, merges into the site header's search (home-search-merge.tsx); then
 * the live register map on desktop. Mobile has none of the motion and no
 * corner row: a title over the search, then example screenshots. Motion from the v2 homepage, look from the main
 * site. Reference: design/v2/homepage.html.
 */
export function HomeLive({ companyCount }: { companyCount: number | null }) {
  const { t, locale } = useLocaleTranslation();
  const mergeCtx = useHomeSearchMerge();
  const [query, setQuery] = useState('');
  const [flipped, setFlipped] = useState(false);
  const [instant, setInstant] = useState(false);
  const [latest, setLatest] = useState<LiveEvent | null>(null);
  // Null until measured: desktop gets the live map, mobile the screenshots,
  // and neither mounts before the breakpoint is known.
  const [wide, setWide] = useState<boolean | null>(null);

  // The form ref apart from the rest, so the React compiler does not treat
  // the whole search box as a ref and refuse its use during render.
  const { formRef: heroFormRef, ...heroBox } = useCompanySearchBox(query, 'fd-home-suggest');
  const closeHeroList = useRef<() => void>(() => {});

  const rootRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const cornersRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const mergeRef = useRef<HTMLDivElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const flippedRef = useRef(false);
  const flipTimer = useRef<number | null>(null);
  const heroVisible = useRef(true);
  // The observers and the scroll handler outlive renders; they read the
  // latest close() and merge context through refs, updated after each render.
  const mergeCtxRef = useRef(mergeCtx);
  useEffect(() => {
    closeHeroList.current = heroBox.close;
    mergeCtxRef.current = mergeCtx;
  });

  const flip = useCallback(() => {
    if (flipTimer.current) window.clearTimeout(flipTimer.current);
    flipTimer.current = null;
    if (flippedRef.current) return;
    flippedRef.current = true;
    setFlipped(true);
  }, []);

  // Back to the name without playing the flip in reverse.
  const unflip = useCallback(() => {
    if (flipTimer.current) window.clearTimeout(flipTimer.current);
    flipTimer.current = null;
    if (!flippedRef.current || document.activeElement === heroInputRef.current) return;
    closeHeroList.current();
    flippedRef.current = false;
    setInstant(true);
    setFlipped(false);
  }, []);

  useEffect(() => {
    if (!instant) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
    return () => cancelAnimationFrame(id);
  }, [instant]);

  // Two screens: the hero fills the first below the header; from the line
  // above "Gerade eingetragen" to the page end is exactly the second, so
  // scrolled all the way down that line sits under the header. The header
  // (desktop row or mobile rows), that seam and the footer are measured.
  useEffect(() => {
    const root = rootRef.current;
    const hero = heroRef.current;
    const corners = cornersRef.current;
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    if (!root || !hero || !corners || !header) return;
    const set = () => {
      root.style.setProperty('--home-header-h', `${header.getBoundingClientRect().height}px`);
      root.style.setProperty('--home-seam-h', `${hero.getBoundingClientRect().bottom - corners.getBoundingClientRect().top}px`);
      root.style.setProperty('--home-footer-h', `${footer?.getBoundingClientRect().height ?? 0}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    for (const el of [header, hero, corners, footer]) if (el) ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Shorter placeholder, and the screenshots instead of the map, below 1024px.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Mobile: while the hero search has focus the site header folds away, so
  // the search and its suggestions get the screen above the keyboard; the
  // title gets a little more room above it in exchange.
  useEffect(() => {
    if (wide !== false) return;
    const input = heroInputRef.current;
    const stage = stageRef.current;
    const header = document.querySelector<HTMLElement>('header');
    if (!input || !stage || !header) return;
    stage.style.transition = 'padding-top 200ms ease';
    const clear = () => {
      header.style.maxHeight = '';
      header.style.overflow = '';
      header.style.transition = '';
      header.style.borderBottomWidth = '';
    };
    const onFocus = () => {
      header.style.overflow = 'hidden';
      header.style.maxHeight = `${header.scrollHeight}px`;
      void header.offsetHeight; // start the transition from the full height
      header.style.transition = 'max-height 200ms ease';
      header.style.maxHeight = '0px';
      header.style.borderBottomWidth = '0px';
      stage.style.paddingTop = '64px';
    };
    const onBlur = () => {
      stage.style.paddingTop = '';
      header.style.borderBottomWidth = '';
      header.style.maxHeight = `${header.scrollHeight}px`;
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target === header && document.activeElement !== input) clear();
    };
    input.addEventListener('focus', onFocus);
    input.addEventListener('blur', onBlur);
    header.addEventListener('transitionend', onEnd);
    return () => {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
      header.removeEventListener('transitionend', onEnd);
      clear();
      stage.style.transition = '';
      stage.style.paddingTop = '';
    };
  }, [wide]);

  // The name shows first, then flips into the search; scrolled away, it resets.
  // Desktop only: on mobile the search shows from the start (CSS).
  useEffect(() => {
    if (wide !== true) return;
    const stage = stageRef.current;
    const hero = heroRef.current;
    if (!stage || !hero) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let firstView = true;
    const so = new IntersectionObserver(
      (es) => {
        const e = es[0];
        if (!e.isIntersecting) {
          unflip();
          return;
        }
        if (e.intersectionRatio >= 0.5 && !flippedRef.current && !flipTimer.current) {
          flipTimer.current = window.setTimeout(flip, reduce ? 0 : firstView ? 600 : 500);
          firstView = false;
        }
      },
      { threshold: [0, 0.5] },
    );
    so.observe(stage);
    const ho = new IntersectionObserver((es) => {
      heroVisible.current = es[0].isIntersecting;
    });
    ho.observe(hero);
    return () => {
      so.disconnect();
      ho.disconnect();
      if (flipTimer.current) window.clearTimeout(flipTimer.current);
    };
  }, [flip, unflip, wide]);

  // Scrolling down shrinks the hero search away and reveals the header's
  // search in its place; at 70% the header search takes over (focusable).
  // Desktop only: on mobile the hero search stays put, so the scroll that
  // brings the focused input into view never shrinks it while typing.
  useEffect(() => {
    if (wide !== true) return;
    const mergeEl = mergeRef.current;
    let lastY = window.scrollY;
    let isMerged = false;
    let raf = 0;
    const update = () => {
      const stage = stageRef.current;
      const merge = mergeRef.current;
      if (!stage || !merge) return;
      const targets = mergeCtxRef.current?.targets() ?? [];
      const header = targets[0]?.closest('header') ?? document.querySelector('header');
      const navB = header?.getBoundingClientRect().bottom ?? 0;
      const r = stage.getBoundingClientRect();
      let yEnd = r.top + window.scrollY + r.height / 2 - navB;
      const yStart = Math.max(0, yEnd - Math.min(260, window.innerHeight * 0.35));
      if (yEnd - yStart < 60) yEnd = yStart + 60;
      const p = Math.min(1, Math.max(0, (window.scrollY - yStart) / (yEnd - yStart)));
      const goingDown = window.scrollY > lastY;
      lastY = window.scrollY;
      if (goingDown && p > 0.02 && p < 0.98 && !flippedRef.current) flip();
      merge.style.opacity = String(1 - p);
      merge.style.transform = p ? `translateY(${(-p * 28).toFixed(1)}px) scale(${(1 - 0.3 * p).toFixed(3)})` : '';
      for (const ns of targets) {
        ns.style.opacity = String(Math.min(1, Math.max(0, (p - 0.25) / 0.75)));
        ns.style.clipPath = p >= 0.99 ? 'none' : `inset(-2px ${((1 - p) * 100).toFixed(1)}% -2px 0)`;
      }
      const on = p >= 0.7;
      if (on !== isMerged) {
        isMerged = on;
        mergeCtxRef.current?.setMerged(on);
        if (!on && targets.some((ns) => ns.contains(document.activeElement))) {
          (document.activeElement as HTMLElement | null)?.blur();
        }
      }
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
      if (mergeEl) {
        mergeEl.style.opacity = '';
        mergeEl.style.transform = '';
      }
    };
  }, [flip, wide]);

  const searchShown = flipped || wide === false;
  const placeholder = wide !== false ? t('homeV2.search.placeholder') : t('homeV2.search.placeholderShort');
  const isHeroVisible = useCallback(() => heroVisible.current, []);

  return (
    <div ref={rootRef} className={styles.root}>
      <section ref={heroRef} className={`${styles.hero} ${styles.pad}`}>
        <div
          ref={stageRef}
          className={`${styles.heroStage} ${flipped ? styles.flipped : ''} ${instant ? styles.noanim : ''}`}
        >
          <h1 className={styles.heroTitle}>{t('homeV2.hero.title')}</h1>
          <h1 className={`${styles.heroWord} ${styles.wordmark}`} aria-label={WORD}>
            <button
              type="button"
              className={styles.heroWord}
              title={t('homeV2.hero.toSearch')}
              aria-label={t('homeV2.hero.toSearch')}
              onClick={() => {
                flip();
                window.setTimeout(() => heroInputRef.current?.focus({ preventScroll: true }), 900);
              }}
            >
              {[...WORD].map((ch, i) => (
                <span
                  key={i}
                  className={`${styles.ch} ${i < BOLD_CHARS ? styles.bold : ''}`}
                  style={{ transitionDelay: `${i * 38}ms` }}
                  aria-hidden="true"
                >
                  {ch}
                </span>
              ))}
            </button>
          </h1>
          <form ref={heroFormRef} className={styles.heroSearch} role="search" autoComplete="off" onSubmit={heroBox.onSubmit}>
            <label className={styles.vh} htmlFor="fd-home-q">{t('homeV2.search.label')}</label>
            <div ref={mergeRef} className={styles.merge}>
              <div className={styles.searchRow}>
                <input
                  ref={heroInputRef}
                  id="fd-home-q"
                  name="q"
                  type="search"
                  placeholder={placeholder}
                  value={query}
                  tabIndex={searchShown ? 0 : -1}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    heroBox.open();
                  }}
                  {...heroBox.inputProps}
                />
                <button type="submit" tabIndex={searchShown ? 0 : -1}>{t('homeV2.search.button')}</button>
              </div>
              <div className={styles.searchHint}>
                <span>
                  {companyCount
                    ? t('homeV2.search.hintCount', {
                        count: new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(companyCount),
                      })
                    : t('homeV2.search.hintTry')}{' '}
                  {TRY_EXAMPLES.map((ex, i) => (
                    <span key={ex}>
                      {i > 0 && ', '}
                      <button
                        type="button"
                        tabIndex={searchShown ? 0 : -1}
                        onClick={() => {
                          setQuery(ex);
                          heroBox.open();
                          heroInputRef.current?.focus();
                        }}
                      >
                        {ex}
                      </button>
                    </span>
                  ))}
                </span>
              </div>
            </div>
            <SuggestList box={heroBox} listId="fd-home-suggest" />
          </form>
        </div>
        <div ref={cornersRef} className={styles.corners}>
          <div className={styles.c1}>
            <span className={styles.bars} aria-hidden="true"><i /><i /><i /><i /><i /></span>
            <span style={{ minWidth: 0 }}>
              <small>{t('homeV2.hero.justRegistered')}</small>
              {latest ? (
                <Link className={styles.now} href={liveEventHref(latest, locale)}>{`${latest.name}, ${latest.city}`}</Link>
              ) : (
                <span className={styles.now}>–</span>
              )}
            </span>
          </div>
          <p className={styles.c2}>{t('homeV2.hero.tagline')}</p>
          <div className={styles.c3}>
            <LocaleLink href="/developer/apis">{t('homeV2.hero.docs')}</LocaleLink>
            <a href={SIGNUP_HREF}>{t('homeV2.hero.tryFree')} →</a>
          </div>
        </div>
      </section>

      {wide === true && (
        <LiveSection locale={locale} onEvent={setLatest} isHeroVisible={isHeroVisible} companyCount={companyCount} />
      )}
      {wide === false && <ProfileShowcase />}
    </div>
  );
}
